import { useNavigate } from "react-router-dom";
import { EmptyState, ListingLinkButton, Panel } from "@/components/common/Primitives";
import { MarketStatusBadge } from "@/components/opportunities/StatusBadges";
import { dateTime, eur, eurSigned, vehicleTitle } from "@/lib/format";

const PERIODS = [
  { key: "since_last_scan", label: "Sinds laatste scan" },
  { key: "today", label: "Vandaag" },
  { key: "7d", label: "7 dagen" },
  { key: "30d", label: "30 dagen" },
];

export const ChangesView = ({ period, onPeriod, events, loading, since }) => {
  const navigate = useNavigate();
  return (
    <Panel
      title="Nieuw &amp; gewijzigd"
      subtitle={since ? `Marktwijzigingen sinds ${dateTime(since)}` : "Marktwijzigingen"}
      actions={
        <div className="flex flex-wrap gap-1">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => onPeriod(p.key)}
              data-testid={`changes-period-${p.key}`}
              className={`rounded-sm border px-2 py-1 text-[11px] font-semibold transition-colors duration-150 ${
                period === p.key
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      }
      testid="changes-view"
    >
      {loading ? (
        <div className="px-4 py-8 text-center text-[13px] text-slate-500">Laden…</div>
      ) : events.length === 0 ? (
        <div className="p-3">
          <EmptyState
            title="Geen wijzigingen in deze periode"
            description="Zodra een verversing nieuwe advertenties, prijsverlagingen of verdwenen auto's vindt, verschijnen die hier."
            testid="changes-empty"
          />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Moment</th>
                <th>Status</th>
                <th>Voertuig</th>
                <th className="text-right">Vraagprijs</th>
                <th className="text-right">Wijziging</th>
                <th className="text-right">Bijdrage</th>
                <th className="text-right">Deal Score</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event, i) => {
                const row = event.row;
                const v = row?.vehicle_snapshot || {};
                const h = row?.headline || {};
                return (
                  <tr
                    key={`${event.listing_id}-${i}`}
                    className={row ? "cursor-pointer" : ""}
                    data-testid={`change-row-${event.listing_id}`}
                    onClick={() => row && navigate(`/auto/${event.listing_id}`)}
                  >
                    <td className="num text-[11.5px]">{dateTime(event.observed_at)}</td>
                    <td>
                      <MarketStatusBadge status={event.status} />
                    </td>
                    <td className="max-w-[260px] truncate text-[12.5px] font-semibold text-slate-900">
                      {row ? vehicleTitle(v) : event.nav_label || "Onbekend voertuig"}
                    </td>
                    <td className="num text-right">{eur(event.price ?? v.price, { fallback: "—" })}</td>
                    <td
                      className={`num text-right ${
                        (event.price_change ?? 0) < 0 ? "text-red-700" : "text-slate-600"
                      }`}
                    >
                      {event.price_change ? eurSigned(event.price_change) : "—"}
                    </td>
                    <td
                      className={`num text-right font-semibold ${
                        (h.contribution ?? 0) > 0 ? "text-emerald-700" : "text-slate-600"
                      }`}
                    >
                      {eurSigned(h.contribution, { fallback: "—" })}
                    </td>
                    <td className="num text-right font-semibold">{h.deal_score ?? "—"}</td>
                    <td>
                      <ListingLinkButton url={v.url} testid={`change-url-${event.listing_id}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
};
